import React, { useState } from 'react';

// Define types for our component
interface SqlToGraphQLProps { }

// Define database types
type ColumnDefinition = {
    name: string;
    type: string;
};

type DatabaseTable = {
    name: string;
    columns: ColumnDefinition[];
    rows: any[][];
};

// Mock database for demo purposes
const mockDatabase: Record<string, DatabaseTable> = {
    users: {
        name: 'users',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'email', type: 'string' },
            { name: 'created_at', type: 'date' }
        ],
        rows: [
            [1, 'John Doe', 'john@example.com', '2023-01-15'],
            [2, 'Jane Smith', 'jane@example.com', '2023-02-20'],
            [3, 'Bob Johnson', 'bob@example.com', '2023-03-10'],
            [4, 'Alice Brown', 'alice@example.com', '2023-04-05'],
            [5, 'Charlie Davis', 'charlie@example.com', '2023-05-12']
        ]
    },
    products: {
        name: 'products',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'name', type: 'string' },
            { name: 'price', type: 'number' },
            { name: 'category', type: 'string' },
            { name: 'in_stock', type: 'boolean' }
        ],
        rows: [
            [101, 'Laptop', 1299.99, 'Electronics', true],
            [102, 'Smartphone', 899.99, 'Electronics', true],
            [103, 'Headphones', 199.99, 'Electronics', false],
            [104, 'Desk Chair', 249.99, 'Furniture', true],
            [105, 'Coffee Table', 349.99, 'Furniture', true],
            [106, 'Bookshelf', 199.99, 'Furniture', false],
            [107, 'Water Bottle', 24.99, 'Kitchen', true],
            [108, 'Blender', 89.99, 'Kitchen', true]
        ]
    },
    orders: {
        name: 'orders',
        columns: [
            { name: 'id', type: 'number' },
            { name: 'user_id', type: 'number' },
            { name: 'total', type: 'number' },
            { name: 'status', type: 'string' },
            { name: 'order_date', type: 'date' }
        ],
        rows: [
            [1001, 1, 1299.99, 'Delivered', '2023-06-15'],
            [1002, 2, 899.99, 'Shipped', '2023-06-20'],
            [1003, 3, 249.99, 'Processing', '2023-06-25'],
            [1004, 1, 89.99, 'Delivered', '2023-07-01'],
            [1005, 4, 549.98, 'Shipped', '2023-07-05'],
            [1006, 5, 199.99, 'Processing', '2023-07-10']
        ]
    }
};

// Function to convert SQL type to GraphQL type
const sqlTypeToGraphQLType = (sqlType: string): string => {
    switch (sqlType.toLowerCase()) {
        case 'number':
        case 'int':
        case 'integer':
        case 'float':
        case 'decimal':
        case 'numeric':
            return 'Float';
        case 'string':
        case 'varchar':
        case 'char':
        case 'text':
        case 'enum':
        case 'date':
        case 'datetime':
        case 'timestamp':
            return 'String';
        case 'boolean':
        case 'bool':
            return 'Boolean';
        default:
            return 'String';
    }
};

// Function to identify primary key columns
const identifyPrimaryKey = (table: DatabaseTable): string | null => {
    // In our simplified model, assume columns named 'id' are primary keys
    const idColumn = table.columns.find(col => col.name.toLowerCase() === 'id');
    return idColumn ? idColumn.name : null;
};

// Function to identify foreign key relationships
const identifyForeignKeys = (tables: Record<string, DatabaseTable>): Record<string, { table: string, column: string }[]> => {
    const relationships: Record<string, { table: string, column: string }[]> = {};
    
    // Initialize relationships object
    Object.keys(tables).forEach(tableName => {
        relationships[tableName] = [];
    });
    
    // Look for potential foreign keys based on naming convention (e.g., user_id)
    Object.keys(tables).forEach(tableName => {
        const table = tables[tableName];
        
        table.columns.forEach(column => {
            // Check if column name follows the pattern table_id
            const match = column.name.match(/^(.+)_id$/);
            if (match) {
                const referencedTableName = match[1];
                
                // Check if this is a valid table name (singular or plural form)
                let targetTable = tables[referencedTableName];
                if (!targetTable) {
                    // Try singular/plural variations
                    const singularForm = referencedTableName.endsWith('s') 
                        ? referencedTableName.slice(0, -1) 
                        : referencedTableName;
                    const pluralForm = referencedTableName.endsWith('s') 
                        ? referencedTableName 
                        : referencedTableName + 's';
                    
                    targetTable = tables[singularForm] || tables[pluralForm];
                }
                
                if (targetTable) {
                    // Found a potential foreign key relationship
                    relationships[tableName].push({
                        table: targetTable.name,
                        column: column.name
                    });
                }
            }
        });
    });
    
    return relationships;
};

// Function to analyze column and determine if it should be non-nullable
const isNonNullable = (column: ColumnDefinition, isPrimaryKey: boolean): boolean => {
    // Primary keys are always non-nullable
    if (isPrimaryKey) return true;
    
    // Make reasonable assumptions based on column name and type
    const name = column.name.toLowerCase();
    
    // Assume that certain columns are required
    if (name === 'name' || name === 'email' || name === 'status' || name === 'category') {
        return true;
    }
    
    return false;
};

// Parse SQL query to determine tables and columns to include in GraphQL schema
const parseSqlForSchema = (sqlQuery: string, database: Record<string, DatabaseTable>) => {
    sqlQuery = sqlQuery.trim();

    // Basic SELECT statement parsing
    if (sqlQuery.toLowerCase().startsWith('select')) {
        // Very basic parsing - not a production-ready SQL parser
        const selectMatch = sqlQuery.match(/select\s+(.*?)\s+from\s+(\w+)(?:\s+where\s+(.*?))?(?:\s+order\s+by\s+(.*?))?(?:\s+limit\s+(\d+))?;?\s*$/i);

        if (!selectMatch) {
            throw new Error('Invalid SQL query format. Expected: SELECT columns FROM table [WHERE condition] [ORDER BY columns] [LIMIT number]');
        }

        const columns = selectMatch[1].trim();
        const tableName = selectMatch[2].trim();

        // Check if table exists
        if (!database[tableName]) {
            throw new Error(`Table '${tableName}' does not exist`);
        }

        const table = database[tableName];

        // Parse columns
        let selectedColumns: number[] = [];
        let columnNames: string[] = [];

        if (columns === '*') {
            // Select all columns
            selectedColumns = table.columns.map((_, index) => index);
            columnNames = table.columns.map(col => col.name);
        } else {
            // Select specific columns
            const colList = columns.split(',').map(col => col.trim());

            colList.forEach(colName => {
                const colIndex = table.columns.findIndex(col => col.name === colName);
                if (colIndex === -1) {
                    throw new Error(`Column '${colName}' does not exist in table '${tableName}'`);
                }
                selectedColumns.push(colIndex);
                columnNames.push(colName);
            });
        }

        return {
            tableName,
            columnNames,
            columnIndices: selectedColumns,
            fullTable: table,
        };
    } else {
        throw new Error('Only SELECT queries are supported for schema generation');
    }
};

// Generate GraphQL schema types from SQL table definitions
const generateGraphQLSchema = (
    sqlAnalysis: { 
        tableName: string; 
        columnNames: string[]; 
        columnIndices: number[]; 
        fullTable: DatabaseTable 
    },
    database: Record<string, DatabaseTable>,
    options: {
        includeQueries: boolean;
        includeMutations: boolean;
        includeSubscriptions: boolean;
    }
): string => {
    const { tableName, columnNames, columnIndices, fullTable } = sqlAnalysis;
    const { includeQueries, includeMutations, includeSubscriptions } = options;
    
    // Identify primary key
    const primaryKey = identifyPrimaryKey(fullTable);
    
    // Identify foreign key relationships
    const relationships = identifyForeignKeys(database);
    
    // Capitalize the table name for the type name
    const typeName = tableName.charAt(0).toUpperCase() + tableName.slice(1);
    // For plural types (e.g., users -> User)
    const singularTypeName = typeName.endsWith('s') ? typeName.slice(0, -1) : typeName;
    
    // Build the type definition
    let schema = `# GraphQL schema generated from SQL\n\n`;
    
    // Type definitions
    schema += `type ${singularTypeName} {\n`;
    
    // Add fields based on selected columns
    for (let i = 0; i < columnNames.length; i++) {
        const colIndex = columnIndices[i];
        const column = fullTable.columns[colIndex];
        const graphqlType = sqlTypeToGraphQLType(column.type);
        const isRequired = isNonNullable(column, column.name === primaryKey);
        
        schema += `  ${column.name}: ${graphqlType}${isRequired ? '!' : ''}\n`;
    }
    
    // Add relationship fields
    const tableRelationships = relationships[tableName] || [];
    tableRelationships.forEach(rel => {
        const relatedTable = database[rel.table];
        if (relatedTable) {
            const relatedTypeName = relatedTable.name.charAt(0).toUpperCase() + relatedTable.name.slice(1);
            const singularRelatedTypeName = relatedTypeName.endsWith('s') ? relatedTypeName.slice(0, -1) : relatedTypeName;
            
            schema += `  ${rel.table.endsWith('s') ? rel.table.slice(0, -1) : rel.table}: ${singularRelatedTypeName}\n`;
        }
    });
    
    // Check if other tables have foreign keys pointing to this table
    Object.keys(relationships).forEach(otherTableName => {
        if (otherTableName !== tableName) {
            const otherTableRels = relationships[otherTableName];
            
            otherTableRels.forEach(rel => {
                if (rel.table === tableName) {
                    // This is a reverse relationship
                    // For a table that references this one, we'll add a plural relation field
                    const relatedTypeName = otherTableName.charAt(0).toUpperCase() + otherTableName.slice(1);
                    const singularRelatedTypeName = relatedTypeName.endsWith('s') ? relatedTypeName.slice(0, -1) : relatedTypeName;
                    
                    schema += `  ${otherTableName}: [${singularRelatedTypeName}]\n`;
                }
            });
        }
    });
    
    schema += `}\n\n`;
    
    // Input types for mutations
    if (includeMutations) {
        schema += `input ${singularTypeName}Input {\n`;
        for (let i = 0; i < columnNames.length; i++) {
            const colIndex = columnIndices[i];
            const column = fullTable.columns[colIndex];
            
            // Skip auto-generated columns like ID for inputs
            if (column.name !== primaryKey) {
                const graphqlType = sqlTypeToGraphQLType(column.type);
                const isRequired = isNonNullable(column, false);
                
                schema += `  ${column.name}: ${graphqlType}${isRequired ? '!' : ''}\n`;
            }
        }
        schema += `}\n\n`;
    }
    
    // Query type
    if (includeQueries) {
        schema += `type Query {\n`;
        // Get by ID
        if (primaryKey) {
            schema += `  ${tableName.endsWith('s') ? tableName.slice(0, -1) : tableName}(${primaryKey}: ID!): ${singularTypeName}\n`;
        }
        // Get all
        schema += `  ${tableName}: [${singularTypeName}!]!\n`;
        schema += `}\n\n`;
    }
    
    // Mutation type
    if (includeMutations) {
        schema += `type Mutation {\n`;
        schema += `  create${singularTypeName}(input: ${singularTypeName}Input!): ${singularTypeName}!\n`;
        if (primaryKey) {
            schema += `  update${singularTypeName}(${primaryKey}: ID!, input: ${singularTypeName}Input!): ${singularTypeName}!\n`;
            schema += `  delete${singularTypeName}(${primaryKey}: ID!): Boolean!\n`;
        }
        schema += `}\n\n`;
    }
    
    // Subscription type
    if (includeSubscriptions) {
        schema += `type Subscription {\n`;
        schema += `  ${tableName}Changed: ${singularTypeName}!\n`;
        schema += `}\n\n`;
    }
    
    // Schema definition
    schema += `schema {\n`;
    if (includeQueries) {
        schema += `  query: Query\n`;
    }
    if (includeMutations) {
        schema += `  mutation: Mutation\n`;
    }
    if (includeSubscriptions) {
        schema += `  subscription: Subscription\n`;
    }
    schema += `}\n`;
    
    return schema;
};

// Generate resolver stubs based on the schema
const generateResolvers = (
    sqlAnalysis: { 
        tableName: string; 
        columnNames: string[]; 
        columnIndices: number[]; 
        fullTable: DatabaseTable 
    },
    database: Record<string, DatabaseTable>,
    options: {
        includeQueries: boolean;
        includeMutations: boolean;
        includeSubscriptions: boolean;
    }
): string => {
    const { tableName, fullTable } = sqlAnalysis;
    const { includeQueries, includeMutations, includeSubscriptions } = options;
    
    // Identify primary key
    const primaryKey = identifyPrimaryKey(fullTable);
    
    // Identify foreign key relationships
    const relationships = identifyForeignKeys(database);
    
    // Capitalize the table name for the type name
    const typeName = tableName.charAt(0).toUpperCase() + tableName.slice(1);
    // For plural types (e.g., users -> User)
    const singularTypeName = typeName.endsWith('s') ? typeName.slice(0, -1) : typeName;
    const camelCaseTypeName = singularTypeName.charAt(0).toLowerCase() + singularTypeName.slice(1);
    
    let resolvers = `// GraphQL resolvers generated from SQL schema\n\n`;
    resolvers += `const resolvers = {\n`;
    
    // Type resolvers for relationships
    resolvers += `  ${singularTypeName}: {\n`;
    
    // Add relationship resolvers
    const tableRelationships = relationships[tableName] || [];
    tableRelationships.forEach(rel => {
        const relatedTable = database[rel.table];
        if (relatedTable) {
            const fieldName = rel.table.endsWith('s') ? rel.table.slice(0, -1) : rel.table;
            
            resolvers += `    ${fieldName}: (parent, args, context) => {\n`;
            resolvers += `      // Fetch related ${fieldName} using parent.${rel.column}\n`;
            resolvers += `      return context.dataSources.${fieldName}API.getById(parent.${rel.column});\n`;
            resolvers += `    },\n`;
        }
    });
    
    // Check if other tables have foreign keys pointing to this table
    Object.keys(relationships).forEach(otherTableName => {
        if (otherTableName !== tableName) {
            const otherTableRels = relationships[otherTableName];
            
            otherTableRels.forEach(rel => {
                if (rel.table === tableName) {
                    // This is a reverse relationship
                    resolvers += `    ${otherTableName}: (parent, args, context) => {\n`;
                    resolvers += `      // Fetch all ${otherTableName} related to this ${camelCaseTypeName}\n`;
                    resolvers += `      return context.dataSources.${otherTableName}API.getBy${singularTypeName}Id(parent.${primaryKey || 'id'});\n`;
                    resolvers += `    },\n`;
                }
            });
        }
    });
    
    resolvers += `  },\n\n`;
    
    // Query resolvers
    if (includeQueries) {
        resolvers += `  Query: {\n`;
        
        // Get by ID
        if (primaryKey) {
            const queryName = tableName.endsWith('s') ? tableName.slice(0, -1) : tableName;
            resolvers += `    ${queryName}: (_, { ${primaryKey} }, context) => {\n`;
            resolvers += `      return context.dataSources.${tableName}API.getById(${primaryKey});\n`;
            resolvers += `    },\n`;
        }
        
        // Get all
        resolvers += `    ${tableName}: (_, __, context) => {\n`;
        resolvers += `      return context.dataSources.${tableName}API.getAll();\n`;
        resolvers += `    },\n`;
        
        resolvers += `  },\n\n`;
    }
    
    // Mutation resolvers
    if (includeMutations) {
        resolvers += `  Mutation: {\n`;
        
        // Create
        resolvers += `    create${singularTypeName}: (_, { input }, context) => {\n`;
        resolvers += `      return context.dataSources.${tableName}API.create(input);\n`;
        resolvers += `    },\n`;
        
        // Update
        if (primaryKey) {
            resolvers += `    update${singularTypeName}: (_, { ${primaryKey}, input }, context) => {\n`;
            resolvers += `      return context.dataSources.${tableName}API.update(${primaryKey}, input);\n`;
            resolvers += `    },\n`;
            
            // Delete
            resolvers += `    delete${singularTypeName}: (_, { ${primaryKey} }, context) => {\n`;
            resolvers += `      return context.dataSources.${tableName}API.delete(${primaryKey});\n`;
            resolvers += `    },\n`;
        }
        
        resolvers += `  },\n\n`;
    }
    
    // Subscription resolvers
    if (includeSubscriptions) {
        resolvers += `  Subscription: {\n`;
        resolvers += `    ${tableName}Changed: {\n`;
        resolvers += `      subscribe: (_, __, { pubsub }) => {\n`;
        resolvers += `        return pubsub.asyncIterator(['${tableName.toUpperCase()}_CHANGED']);\n`;
        resolvers += `      },\n`;
        resolvers += `    },\n`;
        resolvers += `  },\n`;
    }
    
    resolvers += `};\n\n`;
    resolvers += `export default resolvers;\n`;
    
    return resolvers;
};

// Main component to convert SQL to GraphQL Schema
const SqlToGraphQL: React.FC<SqlToGraphQLProps> = () => {
    // State for the SQL input
    const [sqlInput, setSqlInput] = useState<string>('');
    // State for the GraphQL schema output
    const [graphqlSchema, setGraphqlSchema] = useState<string>('');
    // State for resolver output
    const [resolvers, setResolvers] = useState<string>('');
    // State for error messages
    const [error, setError] = useState<string>('');
    // State for the file name
    const [fileName, setFileName] = useState<string>('schema');
    // State for loading indicator
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // State for showing database schema
    const [showSchema, setShowSchema] = useState<boolean>(false);
    // State to track if the conversion happened
    const [isSchemaGenerated, setIsSchemaGenerated] = useState<boolean>(false);
    // State for copied indicator
    const [copied, setCopied] = useState<boolean>(false);
    // State for copy type (schema or resolvers)
    const [copyType, setCopyType] = useState<string>('');

    // GraphQL generation options
    const [includeQueries, setIncludeQueries] = useState<boolean>(true);
    const [includeMutations, setIncludeMutations] = useState<boolean>(true);
    const [includeSubscriptions, setIncludeSubscriptions] = useState<boolean>(false);
    const [showResolvers, setShowResolvers] = useState<boolean>(false);

    // Handle SQL input change
    const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
        setSqlInput(e.target.value);
        setError('');
        setIsSchemaGenerated(false);
    };

    // Use example query
    const useExampleQuery = () => {
        const exampleQuery = 'SELECT * FROM products;';
        setSqlInput(exampleQuery);
        setError('');
        setIsSchemaGenerated(false);
        setGraphqlSchema('');
        setResolvers('');
    };

    // Convert SQL to GraphQL Schema
    const convertSqlToGraphQL = () => {
        setError('');
        setIsLoading(true);
        setIsSchemaGenerated(false);
        setGraphqlSchema('');
        setResolvers('');

        if (!sqlInput.trim()) {
            setError('Please enter an SQL query');
            setIsLoading(false);
            return;
        }

        try {
            // Parse the SQL query and analyze it for schema generation
            const sqlAnalysis = parseSqlForSchema(sqlInput, mockDatabase);

            // Generate the GraphQL schema
            const options = {
                includeQueries,
                includeMutations,
                includeSubscriptions
            };

            const schema = generateGraphQLSchema(sqlAnalysis, mockDatabase, options);
            setGraphqlSchema(schema);

            // Generate resolver stubs
            const resolverCode = generateResolvers(sqlAnalysis, mockDatabase, options);
            setResolvers(resolverCode);

            setIsSchemaGenerated(true);
            setIsLoading(false);
        } catch (e) {
            setError(`Error converting SQL to GraphQL schema: ${e instanceof Error ? e.message : String(e)}`);
            setIsLoading(false);
        }
    };

    // Clear all inputs and reset states
    const clearAll = () => {
        setSqlInput('');
        setGraphqlSchema('');
        setResolvers('');
        setError('');
        setFileName('schema');
        setIsSchemaGenerated(false);
    };

    // Copy to clipboard
    const copyToClipboard = (type: 'schema' | 'resolvers') => {
        const contentToCopy = type === 'schema' ? graphqlSchema : resolvers;
        navigator.clipboard.writeText(contentToCopy)
            .then(() => {
                setCopied(true);
                setCopyType(type);
                setTimeout(() => setCopied(false), 2000);
            })
            .catch(() => {
                setError('Failed to copy to clipboard');
            });
    };

    // Download schema
    const downloadSchema = (type: 'schema' | 'resolvers') => {
        const content = type === 'schema' ? graphqlSchema : resolvers;
        const extension = type === 'schema' ? '.graphql' : '.ts';
        const blob = new Blob([content], { type: 'text/plain;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');

        link.href = url;
        link.download = `${fileName || 'schema'}${extension}`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    // Render database schema
    const renderDatabaseSchema = () => {
        return (
            <div className="bg-blue-50 p-4 rounded-md border border-blue-100 text-sm">
                <h3 className="font-medium mb-2">Database Schema</h3>
                {Object.values(mockDatabase).map(table => (
                    <div key={table.name} className="mb-3">
                        <div className="font-medium">{table.name}</div>
                        <div className="pl-4">
                            {table.columns.map((col, index) => (
                                <div key={index}>
                                    {col.name} ({col.type})
                                </div>
                            ))}
                        </div>
                    </div>
                ))}
                <p className="mt-3 text-blue-800">
                    This is a mock database for demonstration. In a real application, you would
                    connect to an actual database or database API.
                </p>
            </div>
        );
    };

    return (
        <div className="max-w-6xl mx-auto">
            <h1 className="text-2xl font-bold mb-6">SQL to GraphQL Schema Converter</h1>

            {/* Description */}
            <div className="mb-6 bg-blue-50 p-4 rounded-md border border-blue-100">
                <p className="text-blue-800">
                    Convert SQL queries to GraphQL schemas and resolvers. This tool analyzes your SQL queries and database structure to generate a GraphQL schema with types, queries, mutations, and subscriptions.
                </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* Input Section */}
                <div className="flex flex-col h-full">
                    <div className="bg-white p-4 rounded-md shadow-sm mb-4 flex-grow">
                        <div className="flex justify-between items-center mb-3">
                            <label htmlFor="sqlInput" className="font-medium text-gray-700">
                                SQL Query
                            </label>
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800"
                                    onClick={useExampleQuery}
                                >
                                    Use Example
                                </button>
                                <button
                                    className="text-sm text-gray-600 hover:text-gray-800"
                                    onClick={() => setSqlInput('')}
                                >
                                    Clear
                                </button>
                            </div>
                        </div>

                        <textarea
                            id="sqlInput"
                            className="w-full h-48 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                            value={sqlInput}
                            onChange={handleInputChange}
                            placeholder="Enter your SQL query here, e.g.,
SELECT * FROM users;"
                        />

                        <div className="mt-3">
                            <button
                                className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                onClick={() => setShowSchema(!showSchema)}
                            >
                                <span>{showSchema ? 'Hide' : 'Show'} Database Schema</span>
                                <svg
                                    xmlns="http://www.w3.org/2000/svg"
                                    className={`h-4 w-4 ml-1 transition-transform ${showSchema ? 'transform rotate-180' : ''}`}
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                </svg>
                            </button>
                        </div>

                        {showSchema && (
                            <div className="mt-3">
                                {renderDatabaseSchema()}
                            </div>
                        )}
                    </div>

                    {/* Configuration */}
                    <div className="bg-white p-4 rounded-md shadow-sm">
                        <div className="mb-4">
                            <label htmlFor="fileName" className="block text-sm font-medium text-gray-700 mb-1">
                                Output File Name
                            </label>
                            <input
                                type="text"
                                id="fileName"
                                className="w-full p-2 border border-gray-300 rounded-md focus:ring-blue-500 focus:border-blue-500"
                                value={fileName}
                                onChange={(e) => setFileName(e.target.value)}
                                placeholder="Enter file name (without extension)"
                            />
                        </div>

                        <div className="space-y-3 p-3 bg-gray-50 rounded-md mb-4">
                            <div className="text-sm font-medium text-gray-700 mb-2">
                                GraphQL Schema Options
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeQueries"
                                    checked={includeQueries}
                                    onChange={(e) => setIncludeQueries(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeQueries" className="ml-2 block text-sm text-gray-700">
                                    Include Queries (getById, getAll)
                                </label>
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeMutations"
                                    checked={includeMutations}
                                    onChange={(e) => setIncludeMutations(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeMutations" className="ml-2 block text-sm text-gray-700">
                                    Include Mutations (create, update, delete)
                                </label>
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="includeSubscriptions"
                                    checked={includeSubscriptions}
                                    onChange={(e) => setIncludeSubscriptions(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="includeSubscriptions" className="ml-2 block text-sm text-gray-700">
                                    Include Subscriptions (real-time updates)
                                </label>
                            </div>
                            
                            <div className="flex items-center">
                                <input
                                    type="checkbox"
                                    id="showResolvers"
                                    checked={showResolvers}
                                    onChange={(e) => setShowResolvers(e.target.checked)}
                                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                />
                                <label htmlFor="showResolvers" className="ml-2 block text-sm text-gray-700">
                                    Generate Resolver Stubs
                                </label>
                            </div>
                        </div>

                        <div className="mt-4 flex justify-between">
                            <button
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
                                onClick={convertSqlToGraphQL}
                                disabled={isLoading}
                            >
                                {isLoading ? (
                                    <div className="flex items-center">
                                        <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                                        </svg>
                                        Processing...
                                    </div>
                                ) : (
                                    'Generate GraphQL Schema'
                                )}
                            </button>

                            <button
                                className="px-4 py-2 bg-gray-200 text-gray-800 rounded-md hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
                                onClick={clearAll}
                            >
                                Clear All
                            </button>
                        </div>
                    </div>
                </div>

                {/* Output Section */}
                <div className="bg-white p-4 rounded-md shadow-sm h-full flex flex-col">
                    <div className="flex justify-between items-center mb-3">
                        <label className="font-medium text-gray-700">
                            GraphQL Schema Output
                        </label>

                        {isSchemaGenerated && (
                            <div className="flex space-x-2">
                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={() => copyToClipboard('schema')}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                    </svg>
                                    {copied && copyType === 'schema' ? 'Copied!' : 'Copy Schema'}
                                </button>

                                <button
                                    className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                    onClick={() => downloadSchema('schema')}
                                >
                                    <svg
                                        xmlns="http://www.w3.org/2000/svg"
                                        className="h-4 w-4 mr-1"
                                        fill="none"
                                        viewBox="0 0 24 24"
                                        stroke="currentColor"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                    </svg>
                                    Download
                                </button>
                            </div>
                        )}
                    </div>

                    <div className="flex-grow flex flex-col">
                        {error && (
                            <div className="bg-red-50 border border-red-200 text-red-700 p-3 rounded-md mb-3">
                                <p className="font-medium">Error</p>
                                <p>{error}</p>
                            </div>
                        )}

                        {!error && !isSchemaGenerated && (
                            <div className="bg-gray-50 border border-gray-200 text-gray-500 p-4 rounded-md flex-grow flex items-center justify-center">
                                <div className="text-center">
                                    <svg xmlns="http://www.w3.org/2000/svg" className="h-12 w-12 mx-auto text-gray-400 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                                    </svg>
                                    <p>
                                        GraphQL schema will appear here after analyzing the SQL query.
                                    </p>
                                </div>
                            </div>
                        )}

                        {!error && isSchemaGenerated && (
                            <div className="flex-grow flex flex-col">
                                <div className="flex-grow flex flex-col">
                                    <textarea
                                        id="graphqlOutput"
                                        className="w-full flex-grow p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                                        value={graphqlSchema}
                                        readOnly
                                    />
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Resolver Output Section (conditionally rendered) */}
                    {showResolvers && isSchemaGenerated && (
                        <div className="mt-6">
                            <div className="flex justify-between items-center mb-3">
                                <label className="font-medium text-gray-700">
                                    Resolver Stubs
                                </label>

                                <div className="flex space-x-2">
                                    <button
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        onClick={() => copyToClipboard('resolvers')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                        {copied && copyType === 'resolvers' ? 'Copied!' : 'Copy Resolvers'}
                                    </button>

                                    <button
                                        className="text-sm text-blue-600 hover:text-blue-800 flex items-center"
                                        onClick={() => downloadSchema('resolvers')}
                                    >
                                        <svg
                                            xmlns="http://www.w3.org/2000/svg"
                                            className="h-4 w-4 mr-1"
                                            fill="none"
                                            viewBox="0 0 24 24"
                                            stroke="currentColor"
                                        >
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                                        </svg>
                                        Download
                                    </button>
                                </div>
                            </div>

                            <textarea
                                id="resolverOutput"
                                className="w-full h-64 p-3 border border-gray-300 rounded-md font-mono text-sm focus:ring-blue-500 focus:border-blue-500"
                                value={resolvers}
                                readOnly
                            />
                        </div>
                    )}

                    {isSchemaGenerated && (
                        <div className="mt-4 bg-green-50 border border-green-200 text-green-700 p-3 rounded-md">
                            <div className="flex items-center mb-2">
                                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <p className="font-medium">GraphQL Schema Successfully Generated!</p>
                            </div>
                            <p className="text-sm">
                                This schema includes GraphQL types, queries, mutations, and relationships inferred from your SQL query and database structure.
                                {showResolvers && " Resolver stubs have also been generated to implement the schema functionality."}
                            </p>
                        </div>
                    )}
                </div>
            </div>

            {/* Information Section */}
            <div className="mt-8 bg-white p-6 rounded-md shadow-sm">
                <h2 className="text-lg font-semibold mb-4">About SQL to GraphQL Conversion</h2>

                <div className="space-y-4">
                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">What This Tool Does</h3>
                        <p className="text-gray-600">
                            This SQL to GraphQL schema converter analyzes your SQL queries and database structure to generate a GraphQL schema. It creates types, queries, mutations, and resolvers based on the tables and relationships detected in your database.
                        </p>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Features</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li><strong>Type Generation</strong> - Creates GraphQL types from table definitions</li>
                            <li><strong>Relationship Detection</strong> - Identifies foreign key relationships between tables</li>
                            <li><strong>Query Generation</strong> - Creates GraphQL queries for fetching data</li>
                            <li><strong>Mutation Generation</strong> - Creates GraphQL mutations for modifying data</li>
                            <li><strong>Subscription Support</strong> - Adds real-time subscription capabilities</li>
                            <li><strong>Resolver Stubs</strong> - Generates TypeScript resolver implementations</li>
                            <li><strong>Schema Download</strong> - Download the generated schema as a .graphql file</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">How It Works</h3>
                        <ol className="list-decimal pl-5 text-gray-600">
                            <li>Enter a SQL query - Use a SELECT statement to specify the tables and columns</li>
                            <li>Configure options - Choose which GraphQL features to include</li>
                            <li>Generate the schema - The tool analyzes your query and database structure</li>
                            <li>Review and customize - Examine the generated schema and resolver stubs</li>
                            <li>Download or copy - Save the schema for use in your GraphQL API</li>
                        </ol>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Limitations of This Demo</h3>
                        <ul className="list-disc pl-5 text-gray-600">
                            <li>Uses a mock in-memory database with predefined data</li>
                            <li>Only supports basic SELECT queries</li>
                            <li>Simplified relationship detection based on naming conventions</li>
                            <li>Basic data type conversion from SQL to GraphQL</li>
                            <li>Generated resolvers are stubs and would need implementation</li>
                        </ul>
                    </div>

                    <div>
                        <h3 className="font-medium text-gray-800 mb-2">Using the Generated Schema</h3>
                        <p className="text-gray-600">
                            To use the generated schema in a GraphQL API:
                        </p>
                        <ol className="list-decimal pl-5 text-gray-600">
                            <li>Save the schema definition to a .graphql or .gql file</li>
                            <li>Use the resolver stubs as a starting point for your implementation</li>
                            <li>Connect the resolvers to your actual database or data source</li>
                            <li>Implement authentication, authorization, and business logic</li>
                            <li>Set up your GraphQL server (Apollo, Express-GraphQL, etc.)</li>
                        </ol>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default SqlToGraphQL;